import React, { useState, useMemo } from "react";
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from "recharts";

// ─────────────────────────────────────────────────────────────
// TOKENS
// ─────────────────────────────────────────────────────────────
const T = {
  bg:"#EEF3FA", white:"#FFFFFF", navy:"#0A1628", ink:"#152744",
  blue:"#1560BD", sky:"#2E8BC0", teal:"#00A878", mint:"#D4F5EC",
  amber:"#F59E0B", rose:"#E03B3B", blush:"#FFF0F0", purple:"#7C3AED",
  lavender:"#EDE9FE", slate:"#5A7293", mist:"#E2EAF4", light:"#EBF2FC",
  orange:"#EA580C",
};

// ─────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────
const TODAY = new Date("2025-10-01");

const TEACHERS = [
  { id:"TCH001", name:"M. Rivera",    fullName:"Maria Rivera",    grade:"3-4", subject:"ELA",      color:"#00A878" },
  { id:"TCH002", name:"J. Thompson",  fullName:"James Thompson",  grade:"4-5", subject:"Math",     color:"#1560BD" },
  { id:"TCH003", name:"A. Patel",     fullName:"Aisha Patel",     grade:"5-6", subject:"ELA/Math", color:"#7C3AED" },
  { id:"TCH004", name:"C. Brooks",    fullName:"Carlos Brooks",   grade:"6-7", subject:"Math",     color:"#F59E0B" },
  { id:"TCH005", name:"S. Kim",       fullName:"Sarah Kim",       grade:"6",   subject:"ELA",      color:"#E03B3B" },
  { id:"TCH006", name:"D. Torres",    fullName:"David Torres",    grade:"7-8", subject:"Math",     color:"#0891B2" },
];

const STUDENTS = [
  // Rivera TCH001
  { id:"S01", name:"Alex M.",    teacherId:"TCH001", grade:3, iep:true,  ml:false, target:true,  iready:445, growth:-2, att:86.7, nysP:1,
    iepDate:"2025-10-18", iepGoal:"Increase ORF to 90 WCPM; improve reading comp to grade level",
    skillGaps:["Reading Comprehension","Vocabulary","Fluency"], sim:43 },
  { id:"S02", name:"Jordan K.",  teacherId:"TCH001", grade:4, iep:false, ml:false, target:false, iready:478, growth:3,  att:94.9, nysP:2, skillGaps:["Fluency","Reading Comprehension"], sim:64 },
  { id:"S03", name:"Sam L.",     teacherId:"TCH001", grade:3, iep:false, ml:true,  target:true,  iready:441, growth:-4, att:88.9, nysP:1, skillGaps:["Reading Comprehension","Vocabulary","Grammar"], sim:38 },
  { id:"S04", name:"Morgan D.",  teacherId:"TCH001", grade:3, iep:false, ml:false, target:false, iready:461, growth:6,  att:91.7, nysP:2, skillGaps:["Fluency","Phonics"], sim:68 },
  { id:"S05", name:"Drew Q.",    teacherId:"TCH001", grade:4, iep:false, ml:false, target:false, iready:558, growth:22, att:98.9, nysP:4, skillGaps:[], sim:92 },
  // Thompson TCH002
  { id:"S06", name:"Riley P.",   teacherId:"TCH002", grade:5, iep:true,  ml:false, target:false, iready:510, growth:12, att:97.2, nysP:2,
    iepDate:"2025-10-08", iepGoal:"Master fraction operations; achieve 80% on grade-level math assessments",
    skillGaps:["Fractions","Algebra"], sim:74 },
  { id:"S07", name:"Casey W.",   teacherId:"TCH002", grade:5, iep:false, ml:false, target:false, iready:541, growth:18, att:98.3, nysP:3, skillGaps:[], sim:81 },
  { id:"S08", name:"Jamie F.",   teacherId:"TCH002", grade:4, iep:true,  ml:false, target:true,  iready:474, growth:9,  att:90.6, nysP:2,
    iepDate:"2025-11-14", iepGoal:"Develop number sense; reach benchmark on iReady diagnostic",
    skillGaps:["Number Sense","Fractions"], sim:58 },
  { id:"S09", name:"Blake S.",   teacherId:"TCH002", grade:5, iep:false, ml:false, target:false, iready:495, growth:8,  att:95.5, nysP:2, skillGaps:["Algebra"], sim:69 },
  // Patel TCH003
  { id:"S10", name:"Quinn R.",   teacherId:"TCH003", grade:5, iep:false, ml:true,  target:true,  iready:452, growth:-1, att:89.4, nysP:1, skillGaps:["Vocabulary","Reading Comprehension","Fluency"], sim:47 },
  { id:"S11", name:"Avery N.",   teacherId:"TCH003", grade:6, iep:true,  ml:false, target:false, iready:468, growth:4,  att:93.9, nysP:2,
    iepDate:"2025-10-22", iepGoal:"Improve written expression; use graphic organizers independently",
    skillGaps:["Fluency","Reading Comprehension"], sim:62 },
  { id:"S12", name:"Taylor B.",  teacherId:"TCH003", grade:6, iep:false, ml:false, target:false, iready:508, growth:15, att:96.1, nysP:3, skillGaps:["Writing"], sim:71 },
  // Brooks TCH004
  { id:"S13", name:"Chris V.",   teacherId:"TCH004", grade:6, iep:false, ml:false, target:true,  iready:440, growth:-3, att:85.6, nysP:1, skillGaps:["Number Sense","Fractions","Algebra"], sim:41 },
  { id:"S14", name:"Dana H.",    teacherId:"TCH004", grade:7, iep:true,  ml:false, target:true,  iready:462, growth:2,  att:92.1, nysP:2,
    iepDate:"2025-12-05", iepGoal:"Master fraction/decimal conversions; participate in small group math discourse",
    skillGaps:["Fractions","Geometry"], sim:55 },
  { id:"S15", name:"Eli W.",     teacherId:"TCH004", grade:6, iep:false, ml:true,  target:false, iready:489, growth:7,  att:94.3, nysP:2, skillGaps:["Algebra"], sim:67 },
  // Kim TCH005
  { id:"S16", name:"Fiona M.",   teacherId:"TCH005", grade:6, iep:false, ml:false, target:false, iready:521, growth:14, att:97.8, nysP:3, skillGaps:["Writing"], sim:78 },
  { id:"S17", name:"Gabe T.",    teacherId:"TCH005", grade:6, iep:true,  ml:false, target:true,  iready:453, growth:1,  att:88.2, nysP:1,
    iepDate:"2025-10-05", iepGoal:"Decode multisyllabic words; reach 95 WCPM fluency benchmark",
    skillGaps:["Reading Comprehension","Fluency","Vocabulary"], sim:49 },
  // Torres TCH006
  { id:"S18", name:"Hana R.",    teacherId:"TCH006", grade:7, iep:false, ml:true,  target:true,  iready:461, growth:5,  att:91.0, nysP:2, skillGaps:["Fractions","Algebra"], sim:57 },
  { id:"S19", name:"Ivan L.",    teacherId:"TCH006", grade:8, iep:false, ml:false, target:false, iready:534, growth:16, att:97.1, nysP:3, skillGaps:[], sim:79 },
  { id:"S20", name:"Jade P.",    teacherId:"TCH006", grade:8, iep:true,  ml:false, target:true,  iready:448, growth:-2, att:87.3, nysP:1,
    iepDate:"2025-10-29", iepGoal:"Build algebraic reasoning; consistently apply problem-solving strategies",
    skillGaps:["Number Sense","Algebra","Geometry"], sim:44 },
];

const GROUPS = [
  { id:"G01", teacherId:"TCH001", tier:"Intensive", subject:"ELA",  students:["S01","S03"], sessions:6, avgGain:18, focusSkills:["Reading Comprehension","Vocabulary"], freq:5, lastSession:"2025-09-25" },
  { id:"G02", teacherId:"TCH001", tier:"Strategic", subject:"ELA",  students:["S02","S04"], sessions:4, avgGain:10, focusSkills:["Fluency","Reading Comprehension"],    freq:3, lastSession:"2025-09-22" },
  { id:"G03", teacherId:"TCH001", tier:"Extension", subject:"ELA",  students:["S05"],       sessions:2, avgGain:14, focusSkills:["Complex Text Analysis"],              freq:1, lastSession:"2025-09-20" },
  { id:"G04", teacherId:"TCH002", tier:"Strategic", subject:"Math", students:["S06","S09"], sessions:4, avgGain:12, focusSkills:["Fractions","Algebra"],                freq:3, lastSession:"2025-09-24" },
  { id:"G05", teacherId:"TCH002", tier:"Intensive", subject:"Math", students:["S08"],       sessions:5, avgGain:8,  focusSkills:["Number Sense","Fractions"],           freq:5, lastSession:"2025-09-25" },
  { id:"G06", teacherId:"TCH002", tier:"Benchmark", subject:"Math", students:["S07"],       sessions:3, avgGain:16, focusSkills:["Algebra"],                            freq:2, lastSession:"2025-09-21" },
  { id:"G07", teacherId:"TCH003", tier:"Intensive", subject:"ELA",  students:["S10","S11"], sessions:5, avgGain:14, focusSkills:["Vocabulary","Reading Comprehension"],  freq:5, lastSession:"2025-09-25" },
  { id:"G08", teacherId:"TCH003", tier:"Benchmark", subject:"ELA",  students:["S12"],       sessions:3, avgGain:11, focusSkills:["Writing"],                            freq:2, lastSession:"2025-09-19" },
  { id:"G09", teacherId:"TCH004", tier:"Intensive", subject:"Math", students:["S13","S14"], sessions:4, avgGain:9,  focusSkills:["Number Sense","Fractions"],           freq:5, lastSession:"2025-09-24" },
  { id:"G10", teacherId:"TCH004", tier:"Strategic", subject:"Math", students:["S15"],       sessions:3, avgGain:13, focusSkills:["Algebra"],                            freq:3, lastSession:"2025-09-22" },
  { id:"G11", teacherId:"TCH005", tier:"Intensive", subject:"ELA",  students:["S17"],       sessions:4, avgGain:7,  focusSkills:["Reading Comprehension","Fluency"],    freq:5, lastSession:"2025-09-25" },
  { id:"G12", teacherId:"TCH005", tier:"Benchmark", subject:"ELA",  students:["S16"],       sessions:3, avgGain:15, focusSkills:["Writing"],                            freq:2, lastSession:"2025-09-20" },
  { id:"G13", teacherId:"TCH006", tier:"Intensive", subject:"Math", students:["S18","S20"], sessions:3, avgGain:6,  focusSkills:["Fractions","Algebra"],                freq:5, lastSession:"2025-09-23" },
  { id:"G14", teacherId:"TCH006", tier:"Extension", subject:"Math", students:["S19"],       sessions:2, avgGain:20, focusSkills:["Advanced Algebra","Statistics"],       freq:1, lastSession:"2025-09-18" },
];

const SESSION_HISTORY = {
  G01:[{s:"S1",avg:38},{s:"S2",avg:44},{s:"S3",avg:50},{s:"S4",avg:55},{s:"S5",avg:60},{s:"S6",avg:65}],
  G02:[{s:"S1",avg:58},{s:"S2",avg:63},{s:"S3",avg:67},{s:"S4",avg:72}],
  G03:[{s:"S1",avg:80},{s:"S2",avg:86}],
  G04:[{s:"S1",avg:62},{s:"S2",avg:67},{s:"S3",avg:71},{s:"S4",avg:75}],
  G05:[{s:"S1",avg:45},{s:"S2",avg:50},{s:"S3",avg:54},{s:"S4",avg:58},{s:"S5",avg:62}],
  G06:[{s:"S1",avg:72},{s:"S2",avg:78},{s:"S3",avg:83}],
  G07:[{s:"S1",avg:40},{s:"S2",avg:46},{s:"S3",avg:52},{s:"S4",avg:57},{s:"S5",avg:62}],
  G08:[{s:"S1",avg:68},{s:"S2",avg:74},{s:"S3",avg:78}],
  G09:[{s:"S1",avg:36},{s:"S2",avg:42},{s:"S3",avg:47},{s:"S4",avg:52}],
  G10:[{s:"S1",avg:60},{s:"S2",avg:65},{s:"S3",avg:70}],
  G11:[{s:"S1",avg:38},{s:"S2",avg:44},{s:"S3",avg:50},{s:"S4",avg:55}],
  G12:[{s:"S1",avg:72},{s:"S2",avg:78},{s:"S3",avg:83}],
  G13:[{s:"S1",avg:34},{s:"S2",avg:40},{s:"S3",avg:46}],
  G14:[{s:"S1",avg:85},{s:"S2",avg:90}],
};

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────
const tierColor = t => ({Intensive:T.rose,Strategic:T.amber,Benchmark:T.blue,Extension:T.teal})[t]||T.slate;
const tierIcon  = t => ({Intensive:"🔴",Strategic:"🟡",Benchmark:"🔵",Extension:"🟢"})[t]||"⚪";

function daysUntil(dateStr) {
  const d = new Date(dateStr);
  return Math.ceil((d - TODAY) / (1000*60*60*24));
}

function iepUrgency(days) {
  if (days <= 7)  return { color:T.rose,   bg:T.blush,    label:"URGENT",   icon:"🚨" };
  if (days <= 14) return { color:T.orange, bg:"#FFF7ED",  label:"THIS WEEK",icon:"⚠️" };
  if (days <= 30) return { color:T.amber,  bg:"#FFFBEB",  label:"UPCOMING", icon:"📅" };
  return           { color:T.teal,   bg:T.mint,     label:"ON TRACK", icon:"✅" };
}

// ─────────────────────────────────────────────────────────────
// SHARED UI
// ─────────────────────────────────────────────────────────────
const Badge = ({text,color}) => (
  <span style={{background:color+"18",color,border:`1px solid ${color}44`,
    borderRadius:99,padding:"2px 9px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>{text}</span>
);

const Card = ({children,style={}}) => (
  <div style={{background:T.white,borderRadius:14,border:`1px solid ${T.mist}`,
    boxShadow:"0 2px 12px rgba(10,22,40,.06)",padding:"18px 20px",...style}}>{children}</div>
);

const KPI = ({label,value,sub,color=T.blue,icon}) => (
  <div style={{background:T.white,borderRadius:12,padding:"14px 16px",
    border:`1px solid ${T.mist}`,borderTop:`3px solid ${color}`,
    boxShadow:"0 1px 8px rgba(10,22,40,.05)"}}>
    <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
      <span style={{fontSize:10,color:T.slate,fontWeight:700,letterSpacing:".06em",textTransform:"uppercase"}}>{label}</span>
      <span style={{fontSize:16}}>{icon}</span>
    </div>
    <div style={{fontSize:26,fontWeight:900,color:T.navy}}>{value}</div>
    {sub && <div style={{fontSize:11,color,fontWeight:600,marginTop:2}}>{sub}</div>}
  </div>
);

const FilterSelect = ({label,value,onChange,options}) => (
  <div style={{display:"flex",flexDirection:"column",gap:4}}>
    <label style={{fontSize:10,fontWeight:700,color:T.slate,textTransform:"uppercase",letterSpacing:".05em"}}>{label}</label>
    <select value={value} onChange={e=>onChange(e.target.value)} style={{
      padding:"7px 10px",borderRadius:8,border:`1px solid ${T.mist}`,
      fontSize:12,color:T.navy,background:T.white,cursor:"pointer",
      fontFamily:"inherit",minWidth:148,
    }}>
      {options.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

// ─────────────────────────────────────────────────────────────
// IEP ALERT PANEL
// ─────────────────────────────────────────────────────────────
function IEPAlertPanel({onDismiss}) {
  const iepStudents = STUDENTS.filter(s=>s.iep && s.iepDate)
    .map(s => {
      const days    = daysUntil(s.iepDate);
      const urgency = iepUrgency(days);
      const teacher = TEACHERS.find(t=>t.id===s.teacherId);
      return {...s, days, urgency, teacher};
    })
    .sort((a,b)=>a.days-b.days);

  const urgent   = iepStudents.filter(s=>s.days<=7);
  const thisWeek = iepStudents.filter(s=>s.days>7  && s.days<=14);
  const upcoming = iepStudents.filter(s=>s.days>14 && s.days<=30);

  return (
    <div style={{
      background:T.white, border:`2px solid ${T.rose}`,
      borderRadius:16, overflow:"hidden",
      boxShadow:`0 4px 24px ${T.rose}22`,
    }}>
      {/* Header */}
      <div style={{
        background:`linear-gradient(135deg, ${T.rose}18, ${T.orange}10)`,
        borderBottom:`1px solid ${T.rose}33`,
        padding:"14px 20px",
        display:"flex", justifyContent:"space-between", alignItems:"center",
      }}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:22}}>🚨</span>
          <div>
            <div style={{fontSize:15,fontWeight:900,color:T.navy}}>IEP Compliance Alerts</div>
            <div style={{fontSize:11,color:T.slate}}>
              {urgent.length} urgent · {thisWeek.length} this week · {upcoming.length} upcoming
            </div>
          </div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {urgent.length > 0 && (
            <div style={{
              background:T.rose, color:"white", borderRadius:99,
              padding:"4px 12px", fontSize:12, fontWeight:800,
              animation:"pulse 1.5s infinite",
            }}>
              {urgent.length} OVERDUE/URGENT
            </div>
          )}
          <button onClick={onDismiss} style={{
            border:`1px solid ${T.mist}`,background:T.white,borderRadius:7,
            padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:600,color:T.slate,
          }}>Dismiss</button>
        </div>
      </div>

      {/* Alert rows */}
      <div style={{maxHeight:400,overflowY:"auto"}}>
        {iepStudents.map(s => (
          <div key={s.id} style={{
            padding:"14px 20px",
            borderBottom:`1px solid ${T.mist}`,
            borderLeft:`4px solid ${s.urgency.color}`,
            background: s.days<=7 ? s.urgency.bg : T.white,
            display:"flex", gap:16, alignItems:"flex-start", flexWrap:"wrap",
          }}>
            {/* Days countdown */}
            <div style={{
              minWidth:72, textAlign:"center",
              background:s.urgency.color+"15", borderRadius:10,
              padding:"8px 10px", border:`1px solid ${s.urgency.color}33`,
            }}>
              <div style={{fontSize:22,fontWeight:900,color:s.urgency.color,lineHeight:1}}>
                {s.days <= 0 ? "!" : s.days}
              </div>
              <div style={{fontSize:9,fontWeight:700,color:s.urgency.color,marginTop:2}}>
                {s.days <= 0 ? "OVERDUE" : "DAYS"}
              </div>
            </div>

            {/* Student info */}
            <div style={{flex:1,minWidth:200}}>
              <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4,flexWrap:"wrap"}}>
                <span style={{fontSize:14,fontWeight:800,color:T.navy}}>{s.name}</span>
                <Badge text={s.urgency.label} color={s.urgency.color}/>
                <Badge text={`IEP Due: ${s.iepDate}`} color={T.slate}/>
                <Badge text={`Gr ${s.grade}`} color={T.slate}/>
              </div>
              <div style={{fontSize:12,color:T.slate,marginBottom:4}}>
                <strong>Teacher:</strong> {s.teacher?.fullName} · {s.teacher?.subject} · Grade {s.teacher?.grade}
              </div>
              <div style={{fontSize:12,color:T.ink,background:T.bg,borderRadius:7,padding:"7px 10px",
                border:`1px solid ${T.mist}`,lineHeight:1.5}}>
                <strong style={{color:s.urgency.color}}>IEP Goal:</strong> {s.iepGoal}
              </div>
            </div>

            {/* Action buttons */}
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <button style={{
                background:s.urgency.color,color:"white",border:"none",
                borderRadius:7,padding:"7px 14px",cursor:"pointer",
                fontSize:11,fontWeight:700,whiteSpace:"nowrap",
              }}>
                📋 Review IEP
              </button>
              <button style={{
                background:T.bg,color:T.slate,border:`1px solid ${T.mist}`,
                borderRadius:7,padding:"7px 14px",cursor:"pointer",
                fontSize:11,fontWeight:600,whiteSpace:"nowrap",
              }}>
                📧 Notify Teacher
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{padding:"12px 20px",background:T.bg,borderTop:`1px solid ${T.mist}`,
        display:"flex",gap:16,flexWrap:"wrap"}}>
        {[
          {icon:"🚨",label:"≤7 days — Urgent action required",color:T.rose},
          {icon:"⚠️",label:"8–14 days — Schedule review this week",color:T.orange},
          {icon:"📅",label:"15–30 days — Plan ahead",color:T.amber},
          {icon:"✅",label:">30 days — On track",color:T.teal},
        ].map(l=>(
          <div key={l.label} style={{display:"flex",alignItems:"center",gap:5}}>
            <span style={{fontSize:14}}>{l.icon}</span>
            <span style={{fontSize:11,color:l.color,fontWeight:600}}>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
export default function AdminProgressMonitor({ onViewTeacherClass }) {
  const [showAlerts,     setShowAlerts]     = useState(true);
  const [filterTeacher,  setFilterTeacher]  = useState("all");
  const [filterGrade,    setFilterGrade]    = useState("all");
  const [filterSubject,  setFilterSubject]  = useState("all");
  const [filterTier,     setFilterTier]     = useState("all");
  const [filterSubgroup, setFilterSubgroup] = useState("all");
  const [filterSkill,    setFilterSkill]    = useState("all");
  const [filterInterventionist, setFilterInterventionist] = useState("all");
  const [viewMode,       setViewMode]       = useState("overview");
  const [selectedGroup,  setSelectedGroup]  = useState(null);
  const [selectedTeacher,setSelectedTeacher]= useState(null);

  // IEP alert badge count
  const urgentIEPs = STUDENTS.filter(s=>s.iep&&s.iepDate&&daysUntil(s.iepDate)<=14).length;

  // All unique skills
  const allSkills = [...new Set(GROUPS.flatMap(g=>g.focusSkills))].sort();

  // Active filter count
  const activeFilters = [filterTeacher,filterGrade,filterSubject,filterTier,
    filterSubgroup,filterSkill,filterInterventionist].filter(f=>f!=="all").length;

  const resetFilters = () => {
    setFilterTeacher("all"); setFilterGrade("all"); setFilterSubject("all");
    setFilterTier("all"); setFilterSubgroup("all"); setFilterSkill("all");
    setFilterInterventionist("all");
  };

  // Filtered students
  const filteredStudents = useMemo(() => STUDENTS.filter(s => {
    if (filterTeacher !== "all" && s.teacherId !== filterTeacher) return false;
    if (filterGrade   !== "all" && String(s.grade) !== filterGrade) return false;
    if (filterSubgroup === "iep"    && !s.iep)    return false;
    if (filterSubgroup === "ml"     && !s.ml)     return false;
    if (filterSubgroup === "target" && !s.target) return false;
    return true;
  }), [filterTeacher,filterGrade,filterSubgroup]);

  // Filtered groups
  const filteredGroups = useMemo(() => GROUPS.filter(g => {
    if (filterTeacher !== "all" && g.teacherId !== filterTeacher) return false;
    if (filterSubject !== "all" && g.subject   !== filterSubject) return false;
    if (filterTier    !== "all" && g.tier       !== filterTier)   return false;
    if (filterInterventionist !== "all" && g.teacherId !== filterInterventionist) return false;
    if (filterSkill   !== "all" && !g.focusSkills.some(sk=>sk===filterSkill)) return false;
    if (filterSubgroup !== "all") {
      const gs = STUDENTS.filter(s=>g.students.includes(s.id));
      if (filterSubgroup==="iep"    && !gs.some(s=>s.iep))    return false;
      if (filterSubgroup==="ml"     && !gs.some(s=>s.ml))     return false;
      if (filterSubgroup==="target" && !gs.some(s=>s.target)) return false;
    }
    return true;
  }), [filterTeacher,filterSubject,filterTier,filterSubgroup,filterSkill,filterInterventionist]);

  // School-wide KPIs
  const totalSessions = filteredGroups.reduce((a,g)=>a+g.sessions,0);
  const avgGain = filteredGroups.length
    ? Math.round(filteredGroups.reduce((a,g)=>a+g.avgGain,0)/filteredGroups.length) : 0;
  const intensiveCount = filteredGroups.filter(g=>g.tier==="Intensive").length;
  const activeTeacherCount = [...new Set(filteredGroups.map(g=>g.teacherId))].length;

  // Teacher summaries
  const teacherSummaries = TEACHERS.map(t => {
    const tGroups = filteredGroups.filter(g=>g.teacherId===t.id);
    const tStudents = filteredStudents.filter(s=>s.teacherId===t.id);
    const totalSes = tGroups.reduce((a,g)=>a+g.sessions,0);
    const avgG = tGroups.length ? Math.round(tGroups.reduce((a,g)=>a+g.avgGain,0)/tGroups.length) : 0;
    return {
      ...t, groups:tGroups, students:tStudents,
      totalSessions:totalSes, avgGain:avgG,
      iepCount: tStudents.filter(s=>s.iep).length,
      mlCount:  tStudents.filter(s=>s.ml).length,
      targetCount: tStudents.filter(s=>s.target).length,
    };
  });

  const selectedTeacherData = selectedTeacher ? teacherSummaries.find(t=>t.id===selectedTeacher) : null;
  const selectedGroupData   = selectedGroup   ? GROUPS.find(g=>g.id===selectedGroup) : null;
  const selectedGroupTeacher= selectedGroupData ? TEACHERS.find(t=>t.id===selectedGroupData.teacherId) : null;
  const selectedGroupStudents = selectedGroupData ? STUDENTS.filter(s=>selectedGroupData.students.includes(s.id)) : [];

  return (
    <div style={{fontFamily:"'DM Sans','Segoe UI',sans-serif",display:"flex",flexDirection:"column",gap:20}}>

      {/* ── Section header ───────────────────────────────────── */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:22}}>📊</span>
            <span style={{fontSize:20,fontWeight:900,color:T.navy}}>School-Wide Progress Monitoring</span>
            {urgentIEPs > 0 && !showAlerts && (
              <button onClick={()=>setShowAlerts(true)} style={{
                background:T.rose,color:"white",border:"none",borderRadius:99,
                padding:"4px 12px",cursor:"pointer",fontSize:11,fontWeight:800,
              }}>🚨 {urgentIEPs} IEP Alert{urgentIEPs>1?"s":""}</button>
            )}
          </div>
          <div style={{fontSize:12,color:T.slate,marginTop:4,marginLeft:32}}>
            All teacher groups · session tracking · subgroup filters · IEP compliance monitoring
          </div>
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {["overview","teacher","group"].map(m=>(
            <button key={m} onClick={()=>setViewMode(m)} style={{
              border:`2px solid ${viewMode===m?T.blue:T.mist}`,
              background:viewMode===m?T.blue:T.white,
              color:viewMode===m?T.white:T.slate,
              borderRadius:8,padding:"6px 14px",cursor:"pointer",
              fontSize:12,fontWeight:700,textTransform:"capitalize",fontFamily:"inherit",
            }}>
              {m==="overview"?"🏫 Overview":m==="teacher"?"👩‍🏫 By Teacher":"🧩 By Group"}
            </button>
          ))}
        </div>
      </div>

      {/* ── IEP ALERTS ───────────────────────────────────────── */}
      {showAlerts && <IEPAlertPanel onDismiss={()=>setShowAlerts(false)}/>}

      {/* ── FILTER BAR ───────────────────────────────────────── */}
      <Card style={{padding:"16px 20px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div style={{fontSize:13,fontWeight:800,color:T.navy,display:"flex",alignItems:"center",gap:8}}>
            🔍 Filters
            {activeFilters > 0 && (
              <span style={{background:T.blue,color:"white",borderRadius:99,padding:"1px 8px",fontSize:11}}>
                {activeFilters} active
              </span>
            )}
          </div>
          {activeFilters > 0 && (
            <button onClick={resetFilters} style={{
              border:`1px solid ${T.mist}`,background:T.bg,borderRadius:7,
              padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:600,color:T.slate,
            }}>✕ Clear All</button>
          )}
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:14}}>
          <FilterSelect label="Teacher / Class" value={filterTeacher}
            onChange={v=>{setFilterTeacher(v);setSelectedTeacher(v!=="all"?v:null);}}
            options={[{value:"all",label:"All Teachers"},...TEACHERS.map(t=>({value:t.id,label:`${t.fullName} (Gr ${t.grade})`}))]}/>
          <FilterSelect label="Grade" value={filterGrade} onChange={setFilterGrade}
            options={[{value:"all",label:"All Grades"},{value:"3",label:"Grade 3"},{value:"4",label:"Grade 4"},
              {value:"5",label:"Grade 5"},{value:"6",label:"Grade 6"},{value:"7",label:"Grade 7"},{value:"8",label:"Grade 8"}]}/>
          <FilterSelect label="Subject" value={filterSubject} onChange={setFilterSubject}
            options={[{value:"all",label:"All Subjects"},{value:"ELA",label:"ELA"},{value:"Math",label:"Math"}]}/>
          <FilterSelect label="Group Type" value={filterTier} onChange={setFilterTier}
            options={[{value:"all",label:"All Groups"},{value:"Intensive",label:"🔴 Intensive"},
              {value:"Strategic",label:"🟡 Strategic"},{value:"Benchmark",label:"🔵 Benchmark"},{value:"Extension",label:"🟢 Extension"}]}/>
          <FilterSelect label="Interventionist" value={filterInterventionist} onChange={setFilterInterventionist}
            options={[{value:"all",label:"All Interventionists"},...TEACHERS.map(t=>({value:t.id,label:t.fullName}))]}/>
          <FilterSelect label="Subgroup" value={filterSubgroup} onChange={setFilterSubgroup}
            options={[{value:"all",label:"All Students"},{value:"iep",label:"🟣 IEP Students"},
              {value:"ml",label:"🔵 Multi-Language Learners"},{value:"target",label:"🟡 Target Group"}]}/>
          <FilterSelect label="Skill Focus" value={filterSkill} onChange={setFilterSkill}
            options={[{value:"all",label:"All Skills"},...allSkills.map(sk=>({value:sk,label:sk}))]}/>
        </div>
      </Card>

      {/* ── KPI STRIP ────────────────────────────────────────── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12}}>
        <KPI label="Active Groups"     value={filteredGroups.length}  sub="matching filters"        color={T.blue}   icon="🧩"/>
        <KPI label="Total Sessions"    value={totalSessions}           sub="logged school-wide"      color={T.teal}   icon="📋"/>
        <KPI label="Avg Score Gain"    value={`+${avgGain}%`}          sub="first → latest session"  color={T.teal}   icon="📈"/>
        <KPI label="Intensive Groups"  value={intensiveCount}          sub="need daily sessions"     color={T.rose}   icon="🔴"/>
        <KPI label="IEP Alerts"        value={urgentIEPs}              sub="due within 14 days"      color={urgentIEPs>0?T.rose:T.teal} icon="🚨"/>
      </div>

      {/* ── OVERVIEW MODE ────────────────────────────────────── */}
      {viewMode==="overview" && (
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
            <Card>
              <div style={{fontSize:14,fontWeight:800,color:T.navy,marginBottom:14}}>Sessions Logged by Teacher</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={teacherSummaries.map(t=>({name:t.name,sessions:t.totalSessions}))}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.mist}/>
                  <XAxis dataKey="name" tick={{fontSize:10,fill:T.slate}}/>
                  <YAxis tick={{fontSize:10,fill:T.slate}}/>
                  <Tooltip contentStyle={{borderRadius:10,fontSize:12}}/>
                  <Bar dataKey="sessions" fill={T.blue} radius={[4,4,0,0]} name="Sessions"/>
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card>
              <div style={{fontSize:14,fontWeight:800,color:T.navy,marginBottom:14}}>Avg Score Gain by Teacher</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={teacherSummaries.map(t=>({name:t.name,gain:t.avgGain,color:t.avgGain>=10?T.teal:T.amber}))}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.mist}/>
                  <XAxis dataKey="name" tick={{fontSize:10,fill:T.slate}}/>
                  <YAxis domain={[0,25]} tick={{fontSize:10,fill:T.slate}}/>
                  <Tooltip contentStyle={{borderRadius:10,fontSize:12}} formatter={v=>[`+${v}%`,"Avg Gain"]}/>
                  <ReferenceLine y={10} stroke={T.amber} strokeDasharray="4 2" label={{value:"Target",fill:T.amber,fontSize:10}}/>
                  <Bar dataKey="gain" name="Avg % Gain">
                    {teacherSummaries.map((t,i)=>(
                      <rect key={i} fill={t.avgGain>=10?T.teal:T.amber}/>
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* All groups list */}
          <Card>
            <div style={{fontSize:14,fontWeight:800,color:T.navy,marginBottom:4}}>
              All Groups — School-Wide Snapshot
              {activeFilters>0 && <span style={{fontSize:11,color:T.blue,marginLeft:8}}>({filteredGroups.length} shown)</span>}
            </div>
            <div style={{fontSize:11,color:T.slate,marginBottom:14}}>Click any group to view full details</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {filteredGroups.map(g => {
                const teacher  = TEACHERS.find(t=>t.id===g.teacherId);
                const students = STUDENTS.filter(s=>g.students.includes(s.id));
                const history  = SESSION_HISTORY[g.id]||[];
                const gain     = history.length>=2 ? history[history.length-1].avg - history[0].avg : 0;
                const iepCount = students.filter(s=>s.iep).length;
                const mlCount  = students.filter(s=>s.ml).length;
                const hasUrgentIEP = students.some(s=>s.iep&&s.iepDate&&daysUntil(s.iepDate)<=14);
                return (
                  <div key={g.id}
                    onClick={()=>{setSelectedGroup(g.id);setViewMode("group");}}
                    style={{
                      background:T.bg,borderRadius:10,padding:"11px 15px",
                      border:`1px solid ${hasUrgentIEP?T.rose:T.mist}`,
                      cursor:"pointer",display:"flex",gap:12,alignItems:"center",
                      flexWrap:"wrap",transition:"all .15s",
                    }}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=tierColor(g.tier)}
                    onMouseLeave={e=>e.currentTarget.style.borderColor=hasUrgentIEP?T.rose:T.mist}
                  >
                    <div style={{
                      width:38,height:38,borderRadius:9,flexShrink:0,
                      background:tierColor(g.tier)+"18",border:`2px solid ${tierColor(g.tier)}44`,
                      display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,
                    }}>{tierIcon(g.tier)}</div>
                    <div style={{minWidth:160}}>
                      <div style={{fontSize:13,fontWeight:800,color:T.navy}}>{teacher?.fullName} — {g.subject}</div>
                      <div style={{fontSize:11,color:T.slate}}>Gr {teacher?.grade} · {g.tier} · {g.students.length} students · {g.freq}×/wk</div>
                    </div>
                    <div style={{flex:1,minWidth:160}}>
                      <div style={{fontSize:10,color:T.slate,fontWeight:700,marginBottom:3}}>FOCUS SKILLS</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:3}}>
                        {g.focusSkills.map((sk,i)=>(
                          <span key={i} style={{fontSize:10,background:tierColor(g.tier)+"15",
                            color:tierColor(g.tier),borderRadius:5,padding:"2px 7px",fontWeight:600}}>
                            {sk}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                      {iepCount>0 && <Badge text={`IEP ×${iepCount}`} color={T.purple}/>}
                      {mlCount>0  && <Badge text={`ML ×${mlCount}`}   color={T.sky}/>}
                      {hasUrgentIEP && <Badge text="⚠ IEP Due Soon" color={T.rose}/>}
                    </div>
                    <div style={{minWidth:110}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                        <span style={{fontSize:10,color:T.slate,fontWeight:600}}>{g.sessions} sessions</span>
                        <span style={{fontSize:11,fontWeight:800,color:gain>=10?T.teal:T.amber}}>+{gain}%</span>
                      </div>
                      <div style={{background:T.mist,borderRadius:99,height:5}}>
                        <div style={{height:5,borderRadius:99,background:gain>=10?T.teal:T.amber,
                          width:`${Math.min(100,(history[history.length-1]?.avg||0))}%`}}/>
                      </div>
                      <div style={{fontSize:10,color:T.slate,marginTop:2}}>Last: {g.lastSession}</div>
                    </div>
                    <div style={{fontSize:10,color:T.blue,fontWeight:700}}>View →</div>
                  </div>
                );
              })}
              {filteredGroups.length===0 && (
                <div style={{textAlign:"center",padding:"32px",color:T.slate}}>
                  <div style={{fontSize:28}}>🔍</div>
                  <div style={{fontWeight:700,marginTop:6}}>No groups match current filters</div>
                  <button onClick={resetFilters} style={{marginTop:10,border:`1px solid ${T.blue}`,
                    background:T.white,borderRadius:7,padding:"6px 14px",cursor:"pointer",
                    fontSize:12,color:T.blue,fontWeight:600}}>Clear Filters</button>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* ── BY TEACHER MODE ──────────────────────────────────── */}
      {viewMode==="teacher" && (
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
            {teacherSummaries.map(t => {
              const urgentIEPsForTeacher = t.students.filter(s=>s.iep&&s.iepDate&&daysUntil(s.iepDate)<=14).length;
              return (
                <div key={t.id}
                  onClick={()=>setSelectedTeacher(selectedTeacher===t.id?null:t.id)}
                  style={{
                    background:selectedTeacher===t.id?t.color+"12":T.white,
                    border:`2px solid ${selectedTeacher===t.id?t.color:urgentIEPsForTeacher>0?T.rose:T.mist}`,
                    borderRadius:12,padding:"16px",cursor:"pointer",transition:"all .15s",
                  }}>
                  <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:12}}>
                    <div style={{width:40,height:40,borderRadius:10,
                      background:t.color+"22",border:`2px solid ${t.color}44`,
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:13,fontWeight:900,color:t.color}}>{t.avatar||t.name[0]}</div>
                    <div>
                      <div style={{fontSize:14,fontWeight:800,color:T.navy}}>{t.fullName}</div>
                      <div style={{fontSize:11,color:T.slate}}>Gr {t.grade} · {t.subject}</div>
                    </div>
                    {urgentIEPsForTeacher>0 && <Badge text={`🚨 ${urgentIEPsForTeacher} IEP`} color={T.rose}/>}
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:10}}>
                    {[
                      {label:"Groups",   val:t.groups.length,      color:t.color},
                      {label:"Sessions", val:t.totalSessions,       color:T.teal},
                      {label:"Avg Gain", val:`+${t.avgGain}%`,      color:t.avgGain>=10?T.teal:T.amber},
                    ].map(m=>(
                      <div key={m.label} style={{textAlign:"center",background:T.bg,borderRadius:7,padding:"6px 0"}}>
                        <div style={{fontSize:16,fontWeight:900,color:m.color}}>{m.val}</div>
                        <div style={{fontSize:9,color:T.slate,fontWeight:600}}>{m.label}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                    {t.iepCount>0    && <Badge text={`IEP: ${t.iepCount}`}      color={T.purple}/>}
                    {t.mlCount>0     && <Badge text={`ML: ${t.mlCount}`}        color={T.sky}/>}
                    {t.targetCount>0 && <Badge text={`Target: ${t.targetCount}`} color={T.amber}/>}
                    {t.groups.filter(g=>g.tier==="Intensive").length>0 &&
                      <Badge text={`🔴 ${t.groups.filter(g=>g.tier==="Intensive").length} Intensive`} color={T.rose}/>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Selected teacher detail */}
          {selectedTeacherData && (
            <Card style={{border:`2px solid ${selectedTeacherData.color}44`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                <div style={{fontSize:16,fontWeight:900,color:T.navy}}>
                  {selectedTeacherData.fullName} — Gr {selectedTeacherData.grade} · {selectedTeacherData.subject}
                </div>
                {onViewTeacherClass && (
                  <button onClick={() => onViewTeacherClass(selectedTeacherData.id)} style={{
                    background:selectedTeacherData.color, border:"none", borderRadius:8,
                    padding:"7px 16px", color:"#FFFFFF", cursor:"pointer", fontSize:11,
                    fontWeight:700, fontFamily:"inherit",
                    boxShadow:`0 2px 10px ${selectedTeacherData.color}44`,
                  }}>
                    Open Teacher Studio →
                  </button>
                )}
              </div>

              {/* IEP alerts for this teacher */}
              {selectedTeacherData.students.filter(s=>s.iep&&s.iepDate).length > 0 && (
                <div style={{marginBottom:16,background:T.blush,borderRadius:10,padding:"12px 16px",border:`1px solid ${T.rose}44`}}>
                  <div style={{fontSize:12,fontWeight:800,color:T.rose,marginBottom:8}}>🚨 IEP Compliance Tracker</div>
                  {selectedTeacherData.students.filter(s=>s.iep&&s.iepDate).map(s=>{
                    const days = daysUntil(s.iepDate);
                    const urg  = iepUrgency(days);
                    return (
                      <div key={s.id} style={{display:"flex",gap:10,alignItems:"center",marginBottom:6,
                        background:urg.bg,borderRadius:8,padding:"7px 10px",border:`1px solid ${urg.color}33`}}>
                        <span style={{fontSize:18}}>{urg.icon}</span>
                        <div style={{flex:1}}>
                          <span style={{fontSize:13,fontWeight:700,color:T.navy}}>{s.name}</span>
                          <span style={{fontSize:11,color:urg.color,fontWeight:600,marginLeft:8}}>Due: {s.iepDate} ({days}d)</span>
                          <div style={{fontSize:11,color:T.slate,marginTop:2}}>{s.iepGoal}</div>
                        </div>
                        <Badge text={urg.label} color={urg.color}/>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Student roster */}
              <div style={{marginBottom:16}}>
                <div style={{fontSize:11,fontWeight:700,color:T.slate,marginBottom:8,textTransform:"uppercase",letterSpacing:".05em"}}>Student Roster</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                  {selectedTeacherData.students.map(s=>{
                    const iepDays = s.iep&&s.iepDate ? daysUntil(s.iepDate) : null;
                    const urg = iepDays !== null ? iepUrgency(iepDays) : null;
                    return (
                      <div key={s.id} style={{
                        background:T.bg,borderRadius:9,padding:"7px 12px",
                        border:`1px solid ${urg&&iepDays<=14?T.rose:T.mist}`,
                      }}>
                        <div style={{fontSize:12,fontWeight:700,color:T.navy}}>{s.name}</div>
                        <div style={{fontSize:10,color:T.slate}}>Gr{s.grade} · iR:{s.iready} · {s.growth>=0?"+":""}{s.growth}</div>
                        <div style={{display:"flex",gap:4,marginTop:4,flexWrap:"wrap"}}>
                          {s.iep    && <Badge text="IEP"    color={T.purple}/>}
                          {s.ml     && <Badge text="ML"     color={T.sky}/>}
                          {s.target && <Badge text="Target" color={T.amber}/>}
                          {urg && iepDays<=14 && <Badge text={`⚠ ${iepDays}d`} color={T.rose}/>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Groups with mini charts */}
              <div style={{fontSize:11,fontWeight:700,color:T.slate,marginBottom:8,textTransform:"uppercase",letterSpacing:".05em"}}>Small Group Sessions</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12}}>
                {selectedTeacherData.groups.map(g => {
                  const history = SESSION_HISTORY[g.id]||[];
                  return (
                    <div key={g.id} style={{
                      background:tierColor(g.tier)+"08",
                      border:`1px solid ${tierColor(g.tier)}33`,
                      borderRadius:10,padding:"12px 14px",
                    }}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                        <div>
                          <div style={{fontSize:13,fontWeight:800,color:T.navy}}>
                            {tierIcon(g.tier)} {g.tier} · {g.subject}
                          </div>
                          <div style={{fontSize:11,color:T.slate}}>
                            {g.students.length} students · {g.sessions} sessions · {g.freq}×/wk
                          </div>
                        </div>
                        <button onClick={()=>{setSelectedGroup(g.id);setViewMode("group");}} style={{
                          border:`1px solid ${tierColor(g.tier)}`,background:"transparent",
                          color:tierColor(g.tier),borderRadius:6,padding:"3px 10px",
                          cursor:"pointer",fontSize:11,fontWeight:700,
                        }}>Details →</button>
                      </div>
                      {history.length>0 && (
                        <ResponsiveContainer width="100%" height={75}>
                          <LineChart data={history}>
                            <XAxis dataKey="s" tick={{fontSize:9,fill:T.slate}}/>
                            <YAxis domain={[20,100]} hide/>
                            <Tooltip contentStyle={{borderRadius:8,fontSize:11}} formatter={v=>[`${v}%`,"Avg"]}/>
                            <ReferenceLine y={65} stroke={T.blue} strokeDasharray="3 2"/>
                            <Line type="monotone" dataKey="avg" stroke={tierColor(g.tier)} strokeWidth={2} dot={{r:3}}/>
                          </LineChart>
                        </ResponsiveContainer>
                      )}
                      <div style={{display:"flex",flexWrap:"wrap",gap:3,marginTop:6}}>
                        {g.focusSkills.map((sk,i)=>(
                          <span key={i} style={{fontSize:10,background:tierColor(g.tier)+"15",
                            color:tierColor(g.tier),borderRadius:4,padding:"1px 6px",fontWeight:600}}>{sk}</span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ── BY GROUP MODE ────────────────────────────────────── */}
      {viewMode==="group" && (
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
            {filteredGroups.map(g=>{
              const t = TEACHERS.find(t=>t.id===g.teacherId);
              return (
                <button key={g.id} onClick={()=>setSelectedGroup(g.id)} style={{
                  border:`2px solid ${selectedGroup===g.id?tierColor(g.tier):T.mist}`,
                  background:selectedGroup===g.id?tierColor(g.tier)+"15":T.white,
                  borderRadius:8,padding:"6px 12px",cursor:"pointer",
                  fontSize:11,fontWeight:700,
                  color:selectedGroup===g.id?tierColor(g.tier):T.slate,
                  fontFamily:"inherit",
                }}>
                  {tierIcon(g.tier)} {t?.name} · {g.tier}
                </button>
              );
            })}
          </div>

          {selectedGroupData ? (
            <Card style={{border:`2px solid ${tierColor(selectedGroupData.tier)}44`}}>
              {/* Header */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:18,flexWrap:"wrap",gap:12}}>
                <div>
                  <div style={{fontSize:16,fontWeight:900,color:T.navy}}>
                    {tierIcon(selectedGroupData.tier)} {selectedGroupData.tier} Group · {selectedGroupData.subject}
                  </div>
                  <div style={{fontSize:12,color:T.slate,marginTop:3}}>
                    Interventionist: {selectedGroupTeacher?.fullName} · Gr {selectedGroupTeacher?.grade} · {selectedGroupData.sessions} sessions · {selectedGroupData.freq}×/wk · Last: {selectedGroupData.lastSession}
                  </div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <Badge text={`${selectedGroupData.freq}× per week`} color={tierColor(selectedGroupData.tier)}/>
                  <Badge text={`+${selectedGroupData.avgGain}% avg gain`} color={T.teal}/>
                </div>
              </div>

              <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:16}}>
                {/* Progress chart */}
                <div>
                  <div style={{fontSize:12,fontWeight:800,color:T.slate,marginBottom:10,textTransform:"uppercase",letterSpacing:".04em"}}>Session-by-Session Progress</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={SESSION_HISTORY[selectedGroupData.id]||[]}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.mist}/>
                      <XAxis dataKey="s" tick={{fontSize:10,fill:T.slate}}/>
                      <YAxis domain={[20,100]} tick={{fontSize:10,fill:T.slate}}/>
                      <Tooltip contentStyle={{borderRadius:10,fontSize:12}} formatter={v=>[`${v}%`,"Group Avg"]}/>
                      <ReferenceLine y={65} stroke={T.blue} strokeDasharray="4 2" label={{value:"Proficiency",fill:T.blue,fontSize:10}}/>
                      <Line type="monotone" dataKey="avg" stroke={tierColor(selectedGroupData.tier)} strokeWidth={2.5} dot={{r:5}}/>
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Student cards */}
                <div>
                  <div style={{fontSize:12,fontWeight:800,color:T.slate,marginBottom:10,textTransform:"uppercase",letterSpacing:".04em"}}>Students</div>
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {selectedGroupStudents.map(s => {
                      const iepDays = s.iep&&s.iepDate ? daysUntil(s.iepDate) : null;
                      const urg = iepDays !== null ? iepUrgency(iepDays) : null;
                      return (
                        <div key={s.id} style={{
                          background:T.bg,borderRadius:9,padding:"10px 12px",
                          border:`1px solid ${urg&&iepDays<=14?T.rose:T.mist}`,
                        }}>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                            <span style={{fontSize:13,fontWeight:700,color:T.navy}}>{s.name}</span>
                            <span style={{fontSize:11,color:s.growth>=0?T.teal:T.rose,fontWeight:700}}>
                              {s.growth>=0?"+":""}{s.growth}
                            </span>
                          </div>
                          <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:s.iep?6:0}}>
                            {s.iep    && <Badge text="IEP"    color={T.purple}/>}
                            {s.ml     && <Badge text="ML"     color={T.sky}/>}
                            {s.target && <Badge text="Target" color={T.amber}/>}
                            <Badge text={`iR:${s.iready}`} color={s.iready<460?T.rose:T.slate}/>
                          </div>
                          {s.iep && s.iepDate && (
                            <div style={{fontSize:11,background:urg.bg,borderRadius:6,padding:"4px 8px",
                              color:urg.color,fontWeight:600,border:`1px solid ${urg.color}33`}}>
                              {urg.icon} IEP Due: {s.iepDate} · {iepDays}d
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Focus skills */}
              <div style={{marginTop:14,background:tierColor(selectedGroupData.tier)+"08",
                borderRadius:10,padding:"12px 16px",border:`1px solid ${tierColor(selectedGroupData.tier)}22`}}>
                <div style={{fontSize:11,fontWeight:700,color:tierColor(selectedGroupData.tier),marginBottom:6,textTransform:"uppercase"}}>
                  Focus Skills
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {selectedGroupData.focusSkills.map((sk,i)=>(
                    <span key={i} style={{fontSize:13,background:tierColor(selectedGroupData.tier)+"18",
                      color:tierColor(selectedGroupData.tier),borderRadius:7,padding:"5px 14px",
                      fontWeight:700,border:`1px solid ${tierColor(selectedGroupData.tier)}33`}}>
                      {i+1}. {sk}
                    </span>
                  ))}
                </div>
              </div>
            </Card>
          ) : (
            <Card style={{textAlign:"center",padding:"40px"}}>
              <div style={{fontSize:32}}>🧩</div>
              <div style={{fontWeight:700,color:T.navy,marginTop:8}}>Select a group above to see its full details</div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
